import React from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowRight, DollarSign, User, Building2, CreditCard } from 'lucide-react';
import { format } from 'date-fns';

const ReferralsModal = ({ referrals, title, onClose, isAdmin }) => {
  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      commission_paid: 'bg-purple-100 text-purple-800'
    };
    return statusClasses[status] || statusClasses.pending;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-gradient-to-r from-purple-50 to-cyan-50">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
            <p className="text-sm text-slate-600 mt-1">{referrals.length} referral{referrals.length !== 1 ? 's' : ''} found</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Referrals List */}
        <div className="flex-1 overflow-y-auto p-6">
          {referrals.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No referrals found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {referrals.map((referral) => (
                <div
                  key={referral.id}
                  className="block bg-white border border-slate-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="font-semibold text-slate-900 text-lg">
                          {referral.merchant_company_name || referral.referred_email}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(referral.status)}`}>
                          {referral.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="flex items-start gap-2">
                          <User className="text-purple-500 mt-1" size={16} />
                          <div>
                            <span className="text-xs text-slate-500">Contact</span>
                            <p className="font-medium text-sm text-slate-700">
                              {referral.merchant_contact_name || 'N/A'}
                            </p>
                            <p className="text-xs text-slate-500">{referral.merchant_contact_email}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <DollarSign className="text-green-500 mt-1" size={16} />
                          <div>
                            <span className="text-xs text-slate-500">Held Reserves</span>
                            <p className="font-medium text-sm text-slate-900">
                              ${referral.held_reserves_amount?.toLocaleString() || '0'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <CreditCard className="text-blue-500 mt-1" size={16} />
                          <div>
                            <span className="text-xs text-slate-500">Processor</span>
                            <p className="font-medium text-sm text-slate-700">
                              {referral.processor_name || 'N/A'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-2">
                          <Building2 className="text-cyan-500 mt-1" size={16} />
                          <div>
                            <span className="text-xs text-slate-500">Referred By</span>
                            <p className="font-medium text-sm text-slate-700">
                              {referral.referrer_name || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {referral.commission_amount && referral.commission_amount > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600">Commission Amount:</span>
                            <span className="font-bold text-purple-600">
                              ${referral.commission_amount.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {referral.case_id && (
                        <div className="mt-3">
                          <Link
                            to={isAdmin ? `/admin/case/${referral.case_id}` : `/case/${referral.case_id}`}
                            className="inline-flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800 font-medium"
                          >
                            View Associated Case
                            <ArrowRight size={14} />
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferralsModal;
